import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { T, sans } from '../Vendors/VendorDetails';

/* In-app authoring for custom questionnaires -- modeled on UpGuard's own custom-questionnaire
   editor (reviewed live in a real UpGuard trial before building this), laid out as a full-screen
   three-pane workspace: an outline on the left (sections + questions, drag to reorder), the
   canvas in the middle (one question expanded for editing at a time, the rest shown compact), and
   a summary on the right (readiness issues, question and risk breakdown).

   Persistence is a single reconciling PUT on Save (see save_questionnaire_items() on the backend):
   edit everything locally, Save commits the whole list atomically, items keep their id (and with
   it any evidence/risks already attached) across edits. Risks are keyed by option text, so after
   the PUT the save also renames risks whose option was renamed and deletes risks whose option (or
   whole choice list) is gone -- otherwise they'd be silently orphaned. Risk severities are captured
   but deliberately NOT wired into any computed score. */

type QuestionType = 'text' | 'radio' | 'checkbox';
type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface Risk {
  id: string;
  item_id: string;
  option_text: string;
  finding: string;
  risk_statement: string;
  description: string;
  severity: Severity;
  request_compensating_control: boolean;
}

// `orig` is the option's text as last saved (null for an option added since) -- used to carry an
// attached risk across a rename.
interface Option { key: string; text: string; orig: string | null }

interface Item {
  id?: string;
  questionKey?: string;
  localKey: string;
  question_text: string;
  question_type: QuestionType;
  options: Option[];
  section: string;
  required: boolean;
  visibility: 'visible' | 'hidden';
  depends_on: string | null;
}

interface ServerItem {
  id: string;
  question_key: string;
  question_text: string;
  question_type: string | null;
  visibility: 'visible' | 'hidden';
  answer_selections: string | null;
  section: string | null;
  depends_on: string | null;
  required: boolean;
}

interface Questionnaire {
  id: string;
  name: string;
  description: string | null;
  source_file: string | null;
  is_active?: boolean;
  items: ServerItem[];
}

interface Issue { key: string; level: 'error' | 'warning'; message: string }

const SEVERITIES: { key: Severity; label: string; color: string }[] = [
  { key: 'critical', label: 'Critical', color: '#DC2626' },
  { key: 'high', label: 'High', color: '#EA580C' },
  { key: 'medium', label: 'Medium', color: '#D97706' },
  { key: 'low', label: 'Low', color: '#CA8A04' },
  { key: 'info', label: 'Info', color: '#2563EB' },
];
const severityMeta = (s: Severity) => SEVERITIES.find(x => x.key === s) || SEVERITIES[2];

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};
const parseSelections = (raw: string | null) => (raw || '').split('\n').map(s => s.replace(/^•\s*/, '').trim()).filter(Boolean);
const formatSelections = (options: Option[]) => options.map(o => o.text.trim()).filter(Boolean).map(t => `• ${t}`).join('\n');
const uid = () => Math.random().toString(36).slice(2);
// "Yes", "Yes." and worded options like "Yes, we have firewalls ..." -- same test the answering page and the
// server use (questionnaireShell.tsx isYes / services/questionnaire_visibility.py).
const isYesText = (t: string) => /^yes\b/i.test(t.trim());
// depends_on may carry a " (n)" suffix on loaded questionnaires -- the key is the part before it.
const triggerKeyOf = (dependsOn: string | null) => {
  if (!dependsOn) return null;
  const idx = dependsOn.indexOf(' (');
  return idx >= 0 ? dependsOn.slice(0, idx) : dependsOn;
};

const serverToLocal = (s: ServerItem): Item => ({
  id: s.id,
  questionKey: s.question_key,
  localKey: s.id,
  question_text: s.question_text,
  question_type: (s.question_type === 'radio' || s.question_type === 'checkbox') ? s.question_type : 'text',
  options: parseSelections(s.answer_selections).map(t => ({ key: uid(), text: t, orig: t })),
  section: s.section || '',
  required: !!s.required,
  visibility: s.visibility,
  depends_on: s.depends_on,
});

const newOptions = (texts: string[]): Option[] => texts.map(t => ({ key: uid(), text: t, orig: null }));
const blankItem = (type: QuestionType, section: string): Item => ({
  localKey: uid(), question_text: '', question_type: type,
  options: type === 'text' ? [] : newOptions(['Yes', 'No']),
  section, required: false, visibility: 'visible', depends_on: null,
});

const TYPES: { key: QuestionType; label: string; hint: string }[] = [
  { key: 'radio', label: 'Single-select', hint: 'Vendor picks exactly one option' },
  { key: 'checkbox', label: 'Multi-select', hint: 'Vendor picks any number of options' },
  { key: 'text', label: 'Text answer', hint: 'Vendor writes a free-form response' },
];
const typeLabel = (t: QuestionType) => TYPES.find(x => x.key === t)!.label;

const I = {
  plus: 'M12 5v14M5 12h14',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6',
  close: 'M6 6l12 12M18 6 6 18',
  up: 'M18 15l-6-6-6 6',
  down: 'M6 9l6 6 6-6',
  back: 'M19 12H5M12 19l-7-7 7-7',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  text: 'M4 6h16M4 12h16M4 18h10',
  radio: 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
  checkbox: 'M4 4h16v16H4zM8 12l3 3 5-6',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
  logic: 'M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM18 9a9 9 0 0 1-9 9',
  warn: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  ok: 'M22 11.1V12a10 10 0 1 1-5.9-9.1M22 4 12 14l-3-3',
  grip: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  layers: 'M12 2 2 7l10 5 10-5-10-5ZM2 17l10 5 10-5M2 12l10 5 10-5',
};
const typeIcon = (t: QuestionType) => (t === 'text' ? I.text : t === 'radio' ? I.radio : I.checkbox);

const Icon = ({ d, size = 14, sw = 2 }: { d: string; size?: number; sw?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d={d} /></svg>
);

const palette = (isDark: boolean) => ({
  ink: isDark ? '#e5e7eb' : T.ink,
  slate: isDark ? '#94a3b8' : T.slate,
  muted: isDark ? '#64748b' : T.muted,
  line: isDark ? '#1f1f1f' : T.bdr,
  lineStrong: isDark ? '#2a2a2a' : '#CBD5E1',
  surface: isDark ? '#0d0d0d' : T.surface,
  surface2: isDark ? '#141414' : T.surface2,
  bg: isDark ? '#000' : T.bg,
  dark: isDark,
  primary: isDark ? '#3B82F6' : '#2563EB',
  gradient: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 55%, #DB2777 100%)',
  accent: isDark ? '#3B82F6' : '#2563EB',
  accentSoft: isDark ? 'rgba(59,130,246,.14)' : '#EFF6FF',
  red: '#DC2626',
  redSoft: isDark ? 'rgba(220,38,38,.12)' : '#FEF2F2',
  amber: isDark ? '#F59E0B' : '#B45309',
  amberSoft: isDark ? 'rgba(245,158,11,.12)' : '#FFFBEB',
  green: isDark ? '#22C55E' : '#15803D',
  greenSoft: isDark ? 'rgba(34,197,94,.12)' : '#F0FDF4',
  hover: isDark ? '#171717' : '#F1F5F9',
});
type Pal = ReturnType<typeof palette>;

// Each question type gets its own colour so a long questionnaire scans at a glance.
const typeColor = (p: Pal, t: QuestionType) => (
  t === 'radio' ? (p.dark ? '#60A5FA' : '#2563EB') : t === 'checkbox' ? (p.dark ? '#A78BFA' : '#7C3AED') : (p.dark ? '#2DD4BF' : '#0D9488')
);
const typeSoft = (p: Pal, t: QuestionType) => `${typeColor(p, t)}${p.dark ? '26' : '14'}`;

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
const btn = (p: Pal, variant: BtnVariant = 'secondary', size: 'sm' | 'md' = 'md'): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: sans, fontWeight: 600,
  cursor: 'pointer', borderRadius: 7, whiteSpace: 'nowrap', lineHeight: 1.2,
  fontSize: size === 'sm' ? 12 : 13, padding: size === 'sm' ? '5px 9px' : '8px 14px',
  border: variant === 'secondary' ? `1px solid ${p.line}` : variant === 'danger' ? `1px solid ${p.red}40` : '1px solid transparent',
  background: variant === 'primary' ? p.primary : variant === 'secondary' ? p.surface : 'transparent',
  color: variant === 'primary' ? '#fff' : variant === 'danger' ? p.red : variant === 'ghost' ? p.slate : p.ink,
});
const iconBtn = (p: Pal, color?: string): React.CSSProperties => ({
  width: 30, height: 30, borderRadius: 7, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  border: '1px solid transparent', background: 'transparent', color: color || p.slate, cursor: 'pointer', padding: 0,
});
const eyebrow = (p: Pal): React.CSSProperties => ({
  fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: p.muted,
});

function validate(items: Item[]): Issue[] {
  const out: Issue[] = [];
  const byQKey = new Map(items.filter(i => i.questionKey).map(i => [i.questionKey as string, i]));
  for (const it of items) {
    if (!it.question_text.trim()) out.push({ key: it.localKey, level: 'error', message: 'Question text is empty' });
    if (it.question_type !== 'text') {
      const texts = it.options.map(o => o.text.trim()).filter(Boolean);
      if (texts.length < 2) out.push({ key: it.localKey, level: 'error', message: 'Needs at least two answer options' });
      const seen = new Set<string>();
      for (const t of texts) {
        const k = t.toLowerCase();
        if (seen.has(k)) { out.push({ key: it.localKey, level: 'error', message: `Duplicate option "${t}"` }); break; }
        seen.add(k);
      }
    }
    const trig = triggerKeyOf(it.depends_on);
    if (trig) {
      const t = byQKey.get(trig);
      // A trigger without a Yes option is valid: the follow-up shows once that trigger has any answer
      // (the same rule the answering page and the server use), so only a missing trigger is a problem.
      if (!t) out.push({ key: it.localKey, level: 'warning', message: 'Its condition points to a question that no longer exists' });
    }
  }
  return out;
}

/* ─────────────────────────────────────────────────────────────────────────────────────────── */

export default function QuestionnaireBuilder() {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const p = palette(isDark);
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';
  const queryClient = useQueryClient();

  const qQuery = useQuery({
    queryKey: ['questionnaire-library', 'detail', questionnaireId],
    queryFn: async () => (await api.get(`/v1/questionnaire-library/${questionnaireId}`)).data as Questionnaire,
    enabled: !!questionnaireId,
  });
  const risksQuery = useQuery({
    queryKey: ['questionnaire-library', 'risks', questionnaireId],
    queryFn: async () => (await api.get(`/v1/questionnaire-library/${questionnaireId}/risks`)).data.risks as Record<string, Risk[]>,
    enabled: !!questionnaireId,
  });

  // Questions vendors have already answered / attached evidence to / commented on: deleting them
  // would lose that data, so the backend refuses and the builder blocks it up front.
  const inUseQuery = useQuery({
    queryKey: ['questionnaire-library', 'in-use', questionnaireId],
    queryFn: async () => (await api.get(`/v1/questionnaire-library/${questionnaireId}/items-in-use`)).data.in_use as Record<string, number>,
    enabled: !!questionnaireId,
  });
  const inUse = inUseQuery.data || {};

  const [items, setItems] = useState<Item[] | null>(null);
  const [meta, setMeta] = useState<{ name: string; description: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [scrollKey, setScrollKey] = useState<string | null>(null);
  const [riskModal, setRiskModal] = useState<{ itemId: string; option: string; questionText: string } | null>(null);
  const [toast, setToast] = useState<{ id: number; msg: string; undo?: () => void } | null>(null);
  const [saveError, setSaveError] = useState('');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [drag, setDrag] = useState<{ key: string; over: string | null; pos: 'before' | 'after' } | null>(null);
  const cardRefs = useRef(new Map<string, HTMLDivElement>());

  useEffect(() => {
    if (qQuery.data && items === null) {
      setItems(qQuery.data.items.map(serverToLocal));
      setMeta({ name: qQuery.data.name, description: qQuery.data.description || '' });
    }
  }, [qQuery.data, items]);

  const risksByItem = risksQuery.data || {};
  const list = items || [];

  const showToast = (msg: string, undo?: () => void) => setToast({ id: Date.now(), msg, undo });
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(cur => (cur?.id === toast.id ? null : cur)), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const metaChanged = !!(meta && qQuery.data && (meta.name !== qQuery.data.name || meta.description !== (qQuery.data.description || '')));
  const issues = useMemo(() => validate(list), [list]);
  const errors = issues.filter(i => i.level === 'error');
  const issuesByKey = useMemo(() => {
    const m = new Map<string, Issue[]>();
    issues.forEach(i => m.set(i.key, [...(m.get(i.key) || []), i]));
    return m;
  }, [issues]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (metaChanged && meta) {
        await api.patch(`/v1/questionnaire-library/${questionnaireId}`, { name: meta.name.trim(), description: meta.description.trim() });
      }
      const payload = list.map(it => ({
        id: it.id, question_text: it.question_text.trim(), question_type: it.question_type,
        answer_selections: it.question_type === 'text' ? null : formatSelections(it.options),
        section: it.section.trim() || null, required: it.required, visibility: it.visibility, depends_on: it.depends_on,
      }));
      const saved = (await api.put(`/v1/questionnaire-library/${questionnaireId}/items`, { items: payload })).data.items as ServerItem[];

      // Carry risks across option renames; remove risks whose option no longer exists.
      const ops: Promise<unknown>[] = [];
      for (const it of list) {
        if (!it.id) continue;
        const itemRisks = risksByItem[it.id] || [];
        const finalTexts = it.question_type === 'text' ? [] : it.options.map(o => o.text.trim()).filter(Boolean);
        for (const r of itemRisks) {
          if (finalTexts.includes(r.option_text)) continue;
          const renamed = it.question_type === 'text' ? undefined
            : it.options.find(o => o.orig === r.option_text && o.text.trim() && !itemRisks.some(x => x.option_text === o.text.trim()));
          if (renamed) {
            ops.push(api.patch(`/v1/questionnaire-library/risks/${r.id}`, {
              option_text: renamed.text.trim(), finding: r.finding, risk_statement: r.risk_statement,
              description: r.description, severity: r.severity, request_compensating_control: r.request_compensating_control,
            }));
          } else {
            ops.push(api.delete(`/v1/questionnaire-library/risks/${r.id}`));
          }
        }
      }
      await Promise.all(ops);
      return saved;
    },
    onSuccess: saved => {
      const selIndex = list.findIndex(it => it.localKey === selectedKey);
      const next = saved.map(serverToLocal);
      setItems(next);
      setSelectedKey(selIndex >= 0 && next[selIndex] ? next[selIndex].localKey : null);
      setFocusKey(null);
      setDirty(false);
      setSaveError('');
      setLastSaved(new Date());
      if (meta) {
        const trimmed = { name: meta.name.trim(), description: meta.description.trim() };
        setMeta(trimmed);
        queryClient.setQueryData<Questionnaire>(['questionnaire-library', 'detail', questionnaireId], (old: Questionnaire | undefined) => (old ? { ...old, ...trimmed } : old));
      }
      queryClient.invalidateQueries({ queryKey: ['questionnaire-library'] });
    },
    onError: err => setSaveError(apiError(err, 'Could not save. Try again.')),
  });

  const publishMutation = useMutation({
    mutationFn: async (is_active: boolean) => api.patch(`/v1/questionnaire-library/${questionnaireId}`, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questionnaire-library'] }),
    onError: err => showToast(apiError(err, 'Could not change the status.')),
  });

  const canSave = canEdit && (dirty || metaChanged) && !saveMutation.isPending;
  const trySave = () => {
    if (!canSave) return;
    if (meta && !meta.name.trim()) { setSaveError('The questionnaire needs a name.'); return; }
    if (errors.length) {
      setSaveError(`Fix ${errors.length} issue${errors.length === 1 ? '' : 's'} before saving.`);
      setSelectedKey(errors[0].key);
      setScrollKey(errors[0].key);
      return;
    }
    saveMutation.mutate();
  };
  const saveRef = useRef(trySave);
  saveRef.current = trySave;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); saveRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const hasUnsaved = dirty || metaChanged;
  useEffect(() => {
    if (!hasUnsaved) return;
    const onBefore = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [hasUnsaved]);

  useEffect(() => {
    if (!scrollKey) return;
    const el = cardRefs.current.get(scrollKey);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setScrollKey(null);
  }, [scrollKey]);

  /* ── list operations ── */
  const mutate = (fn: (l: Item[]) => Item[]) => { setItems(prev => (prev ? fn(prev) : prev)); setDirty(true); setSaveError(''); };
  const updateItem = (key: string, patch: Partial<Item>) => mutate(l => l.map(it => (it.localKey === key ? { ...it, ...patch } : it)));
  const select = (key: string | null, scroll = false) => { setSelectedKey(key); setFocusKey(null); if (scroll && key) setScrollKey(key); };

  const insertAt = (index: number, item: Item) => {
    mutate(l => { const c = [...l]; c.splice(index, 0, item); return c; });
    setSelectedKey(item.localKey);
    setFocusKey(item.localKey);
    setScrollKey(item.localKey);
  };
  const removeItem = (key: string) => {
    const snapshot = list;
    const victim = list.find(x => x.localKey === key);
    const responses = victim?.id ? inUse[victim.id] : 0;
    if (responses) {
      showToast(`Q${numberOf.get(key)} can't be deleted: vendors have responded to it on ${responses} questionnaire${responses === 1 ? '' : 's'}. Edit its wording instead.`);
      return;
    }
    mutate(l => l.filter(it => it.localKey !== key).map(it => (
      victim?.questionKey && triggerKeyOf(it.depends_on) === victim.questionKey ? { ...it, depends_on: null, visibility: 'visible' as const } : it
    )));
    if (selectedKey === key) setSelectedKey(null);
    showToast('Question deleted', () => { setItems(snapshot); setDirty(true); });
  };
  const duplicateItem = (key: string) => {
    const i = list.findIndex(x => x.localKey === key);
    if (i < 0) return;
    const src = list[i];
    insertAt(i + 1, { ...src, id: undefined, questionKey: undefined, localKey: uid(), options: src.options.map(o => ({ key: uid(), text: o.text, orig: null })) });
    showToast('Question duplicated — risks are not copied');
  };
  const moveItem = (key: string, dir: -1 | 1) => mutate(l => {
    const i = l.findIndex(it => it.localKey === key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= l.length) return l;
    const c = [...l];
    // Crossing a section boundary moves the question into the neighbouring section rather than
    // swapping past it.
    if (c[j].section !== c[i].section) { c[i] = { ...c[i], section: c[j].section }; return c; }
    [c[i], c[j]] = [c[j], c[i]];
    return c;
  });
  const moveTo = (dragKey: string, targetKey: string, pos: 'before' | 'after') => {
    if (dragKey === targetKey) return;
    mutate(l => {
      const from = l.findIndex(x => x.localKey === dragKey);
      const target = l.find(x => x.localKey === targetKey);
      if (from < 0 || !target) return l;
      const moving = { ...l[from], section: target.section };
      const rest = l.filter((_, i) => i !== from);
      let to = rest.findIndex(x => x.localKey === targetKey);
      if (pos === 'after') to += 1;
      rest.splice(to, 0, moving);
      return rest;
    });
  };
  const renameSection = (start: number, count: number, name: string) => mutate(l => l.map((it, i) => (i >= start && i < start + count ? { ...it, section: name } : it)));

  const runs = useMemo(() => {
    const out: { section: string; start: number; items: Item[] }[] = [];
    list.forEach((it, i) => {
      const last = out[out.length - 1];
      if (last && last.section === it.section) last.items.push(it);
      else out.push({ section: it.section, start: i, items: [it] });
    });
    return out;
  }, [list]);

  const numberOf = useMemo(() => new Map(list.map((it, i) => [it.localKey, i + 1])), [list]);
  const byQKey = useMemo(() => new Map(list.filter(i => i.questionKey).map(i => [i.questionKey as string, i])), [list]);

  const triggerCandidates = (it: Item) => list.filter(c => {
    if (!c.questionKey || c.localKey === it.localKey || c.question_type === 'text') return false;
    if (!c.options.some(o => isYesText(o.text))) return false;
    // Exclude anything that (transitively) depends on this item, so no cycles.
    let cur: Item | undefined = c;
    for (let guard = 0; cur && guard < 50; guard++) {
      const k = triggerKeyOf(cur.depends_on);
      if (!k) break;
      if (it.questionKey && k === it.questionKey) return false;
      cur = byQKey.get(k);
    }
    return true;
  });

  const risksFor = (it: Item) => (it.id ? risksByItem[it.id] || [] : []);

  const stats = useMemo(() => {
    const total = list.length;
    const live = list.filter(i => i.id);
    const allRisks = live.flatMap(i => risksByItem[i.id as string] || []);
    const bySeverity: Record<Severity, number> = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    allRisks.forEach(r => { bySeverity[r.severity] = (bySeverity[r.severity] || 0) + 1; });
    const choice = list.filter(i => i.question_type !== 'text').length;
    return {
      total,
      required: list.filter(i => i.required).length,
      conditional: list.filter(i => !!i.depends_on).length,
      sections: new Set(list.map(i => i.section.trim()).filter(Boolean)).size,
      byType: { radio: list.filter(i => i.question_type === 'radio').length, checkbox: list.filter(i => i.question_type === 'checkbox').length, text: list.filter(i => i.question_type === 'text').length },
      totalRisks: allRisks.length,
      bySeverity,
      choice,
      withRisks: new Set(allRisks.map(r => r.item_id)).size,
    };
  }, [list, risksByItem]);

  const sectionNames = useMemo(() => Array.from(new Set(list.map(i => i.section).filter(Boolean))), [list]);

  /* ── drag & drop (shared by outline rows and canvas cards) ── */
  const dnd = {
    start: (key: string) => (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', key);
      const card = cardRefs.current.get(key);
      if (card && e.currentTarget !== card) e.dataTransfer.setDragImage(card, 24, 24);
      setDrag({ key, over: null, pos: 'before' });
    },
    over: (key: string) => (e: React.DragEvent) => {
      if (!drag) return;
      e.preventDefault();
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const pos = e.clientY < r.top + r.height / 2 ? 'before' : 'after';
      if (drag.over !== key || drag.pos !== pos) setDrag({ ...drag, over: key, pos });
    },
    drop: (key: string) => (e: React.DragEvent) => {
      e.preventDefault();
      if (drag) moveTo(drag.key, key, drag.pos);
      setDrag(null);
    },
    end: () => setDrag(null),
  };
  const dropLine = (key: string): 'before' | 'after' | null => (drag && drag.over === key && drag.key !== key ? drag.pos : null);

  /* ── early states ── */
  const shell = (children: React.ReactNode) => (
    <div style={{ minHeight: '100vh', background: p.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: sans, padding: 24 }}>
      <div style={{ background: p.surface, border: `1px solid ${p.line}`, borderRadius: 12, padding: 28, textAlign: 'center', maxWidth: 440 }}>{children}</div>
    </div>
  );
  if (qQuery.isPending || (qQuery.data && items === null)) {
    return <div style={{ minHeight: '100vh', background: p.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: sans, color: p.muted, fontSize: 13.5 }}>Loading builder…</div>;
  }
  if (qQuery.isError || !qQuery.data || !meta) {
    return shell(<>
      <div style={{ fontSize: 15, fontWeight: 700, color: p.ink, marginBottom: 12 }}>Could not load this questionnaire</div>
      <button onClick={() => navigate('/questionnaire-library')} style={btn(p)}>Back to library</button>
    </>);
  }
  if (qQuery.data.source_file !== null) {
    return shell(<>
      <div style={{ fontSize: 15, fontWeight: 700, color: p.ink, marginBottom: 6 }}>Only custom questionnaires can be edited here</div>
      <div style={{ fontSize: 13.5, color: p.slate, marginBottom: 16 }}>"{qQuery.data.name}" is part of the standard catalog.</div>
      <button onClick={() => navigate('/questionnaire-library')} style={btn(p)}>Back to library</button>
    </>);
  }

  const goBack = () => {
    if (hasUnsaved && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate('/questionnaire-library');
  };
  const openPreview = () => {
    window.open(`/questionnaire-library/${questionnaireId}/preview`, '_blank', 'noopener');
    if (hasUnsaved) showToast('Preview shows the last saved version — save to include your latest changes.');
  };
  const isPublished = qQuery.data.is_active;

  const saveStatus = saveMutation.isPending ? { text: 'Saving…', color: p.slate }
    : saveError ? { text: saveError, color: p.red }
    : hasUnsaved ? { text: 'Unsaved changes', color: p.amber }
    : lastSaved ? { text: `Saved at ${lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, color: p.green }
    : { text: 'All changes saved', color: p.muted };

  const rootVars = { '--qb-hover': p.hover, '--qb-line': p.line } as React.CSSProperties;

  return (
    <div style={{ ...rootVars, fontFamily: sans, background: p.bg, position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', color: p.ink }}>
      <style>{`
        .qb-grid { display: grid; grid-template-columns: 272px minmax(0, 1fr) 300px; flex: 1; min-height: 0; }
        .qb-pane { overflow-y: auto; min-height: 0; }
        .qb-hov:hover { background: var(--qb-hover) !important; }
        .qb-card .qb-grip { opacity: 0; transition: opacity .12s; }
        .qb-card:hover .qb-grip, .qb-card.qb-sel .qb-grip { opacity: 1; }
        .qb-input:focus { border-color: ${p.accent} !important; box-shadow: 0 0 0 3px ${p.accentSoft}; }
        .qb-ghost-input:hover { background: var(--qb-hover); }
        .qb-ghost-input:focus { background: ${p.surface}; box-shadow: 0 0 0 2px ${p.accent}; }
        @keyframes qbToastIn { from { transform: translate(-50%, 12px); opacity: 0 } to { transform: translate(-50%, 0); opacity: 1 } }
        @media (max-width: 1240px) { .qb-grid { grid-template-columns: 248px minmax(0, 1fr); } .qb-summary { display: none; } }
        @media (max-width: 860px) { .qb-grid { grid-template-columns: minmax(0, 1fr); } .qb-outline { display: none; } .qb-hide-sm { display: none !important; } }
      `}</style>

      {/* ── top bar ── */}
      <header style={{ height: 60, flexShrink: 0, background: p.surface, borderBottom: `1px solid ${p.line}`, padding: '0 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={goBack} className="qb-hov" title="Back to library" style={{ ...iconBtn(p), width: 34, height: 34, border: `1px solid ${p.line}` }}>
          <Icon d={I.back} />
        </button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="qb-hide-sm" style={{ fontSize: 11.5, color: p.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ cursor: 'pointer' }} onClick={goBack}>Questionnaire Library</span>
            <span>/</span>
            <span>Builder</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: p.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta.name || 'Untitled questionnaire'}</span>
            {typeof isPublished === 'boolean' && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, flexShrink: 0, background: isPublished ? p.greenSoft : p.amberSoft, color: isPublished ? p.green : p.amber, border: `1px solid ${isPublished ? p.green : p.amber}40` }}>
                {isPublished ? 'Published' : 'Draft'}
              </span>
            )}
          </div>
        </div>
        <span className="qb-hide-sm" style={{ fontSize: 12.5, color: saveStatus.color, display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: saveStatus.color, flexShrink: 0 }} />
          {saveStatus.text}
        </span>
        <button onClick={openPreview} style={btn(p)} title="Open the last saved version in a new tab"><Icon d={I.eye} /> Preview</button>
        {canEdit && typeof isPublished === 'boolean' && (
          <button onClick={() => publishMutation.mutate(!isPublished)} disabled={publishMutation.isPending} style={{ ...btn(p), opacity: publishMutation.isPending ? 0.6 : 1 }} className="qb-hide-sm">
            {isPublished ? 'Unpublish' : 'Publish'}
          </button>
        )}
        {canEdit && (
          <button onClick={trySave} disabled={!canSave} title="Save (Ctrl+S)" style={{ ...btn(p, 'primary'), minWidth: 84, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'default' }}>
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </header>

      {!canEdit && (
        <div style={{ background: p.amberSoft, color: p.amber, fontSize: 12.5, padding: '8px 18px', borderBottom: `1px solid ${p.line}` }}>
          You have view-only access. Ask an editor or admin to change this questionnaire.
        </div>
      )}

      <div className="qb-grid">
        {/* ── outline ── */}
        <aside className="qb-pane qb-outline" style={{ background: p.surface, borderRight: `1px solid ${p.line}`, padding: '16px 10px 40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px', marginBottom: 10 }}>
            <span style={eyebrow(p)}>Outline</span>
            <span style={{ fontSize: 11.5, color: p.muted }}>{list.length} question{list.length === 1 ? '' : 's'}</span>
          </div>
          {list.length === 0 && <div style={{ fontSize: 12.5, color: p.muted, padding: '8px 8px' }}>Questions you add appear here.</div>}
          {runs.map(run => (
            <div key={run.start} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: p.slate, padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon d={I.layers} size={12} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{run.section || 'Untitled section'}</span>
                <span style={{ color: p.muted, fontWeight: 600 }}>{run.items.length}</span>
              </div>
              {run.items.map(it => {
                const n = numberOf.get(it.localKey)!;
                const sel = selectedKey === it.localKey;
                const hasErr = (issuesByKey.get(it.localKey) || []).some(x => x.level === 'error');
                const hasWarn = !hasErr && (issuesByKey.get(it.localKey) || []).length > 0;
                const rc = risksFor(it).length;
                const line = dropLine(it.localKey);
                return (
                  <div key={it.localKey}
                    draggable={canEdit}
                    onDragStart={dnd.start(it.localKey)} onDragOver={dnd.over(it.localKey)} onDrop={dnd.drop(it.localKey)} onDragEnd={dnd.end}
                    onClick={() => select(it.localKey, true)}
                    className={sel ? undefined : 'qb-hov'}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 7, cursor: 'pointer',
                      background: sel ? p.accentSoft : 'transparent', marginLeft: it.depends_on ? 14 : 0,
                      boxShadow: line === 'before' ? `inset 0 2px 0 ${p.accent}` : line === 'after' ? `inset 0 -2px 0 ${p.accent}` : undefined,
                      opacity: drag?.key === it.localKey ? 0.4 : 1,
                    }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: sel ? p.accent : p.muted, minWidth: 22, fontVariantNumeric: 'tabular-nums' }}>{it.depends_on ? '↳' : `Q${n}`}</span>
                    <span style={{ color: typeColor(p, it.question_type), display: 'inline-flex' }}><Icon d={typeIcon(it.question_type)} size={12} /></span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: it.question_text.trim() ? (sel ? p.ink : p.slate) : p.muted, fontStyle: it.question_text.trim() ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: sel ? 600 : 400 }}>
                      {it.question_text.trim() || 'Untitled question'}
                    </span>
                    {it.required && <span title="Required" style={{ color: p.red, fontWeight: 700, fontSize: 13 }}>*</span>}
                    {rc > 0 && <span title={`${rc} risk${rc === 1 ? '' : 's'}`} style={{ color: severityMeta(worstSeverity(risksFor(it))).color, display: 'inline-flex' }}><Icon d={I.shield} size={12} /></span>}
                    {hasErr && <span title="Has issues" style={{ color: p.red, display: 'inline-flex' }}><Icon d={I.warn} size={12} /></span>}
                    {hasWarn && <span title="Has warnings" style={{ color: p.amber, display: 'inline-flex' }}><Icon d={I.warn} size={12} /></span>}
                  </div>
                );
              })}
            </div>
          ))}
          {canEdit && list.length > 0 && (
            <button onClick={() => insertAt(list.length, blankItem('radio', nextSectionName(sectionNames)))} className="qb-hov" style={{ ...btn(p, 'ghost', 'sm'), width: '100%', justifyContent: 'flex-start', padding: '8px', marginTop: 4 }}>
              <Icon d={I.plus} size={13} /> New section
            </button>
          )}
        </aside>

        {/* ── canvas ── */}
        <main className="qb-pane" onClick={e => { if (e.target === e.currentTarget) select(null); }} style={{ padding: '28px 28px 120px' }}>
          <div style={{ width: '90%', margin: '0 auto' }} onClick={e => { if (e.target === e.currentTarget) select(null); }}>
            {/* title card */}
            <div style={{ background: p.surface, border: `1px solid ${p.line}`, borderRadius: 12, padding: '20px 22px 16px', marginBottom: 22 }}>
              <input
                value={meta.name} disabled={!canEdit} placeholder="Questionnaire name"
                onChange={e => { setMeta({ ...meta, name: e.target.value }); setSaveError(''); }}
                className="qb-ghost-input"
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 22, fontWeight: 700, color: p.ink, fontFamily: sans, background: 'transparent', border: 'none', outline: 'none', borderRadius: 6, padding: '4px 6px', marginLeft: -6 }}
              />
              <AutoTextarea
                value={meta.description} disabled={!canEdit} placeholder="Add a description — tell vendors what this questionnaire covers and why you're asking"
                onChange={e => setMeta({ ...meta, description: e.target.value })}
                className="qb-ghost-input"
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 13.5, lineHeight: 1.55, color: p.slate, fontFamily: sans, background: 'transparent', border: 'none', outline: 'none', borderRadius: 6, padding: '4px 6px', marginLeft: -6, marginTop: 2 }}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                {[
                  { label: `${stats.total} question${stats.total === 1 ? '' : 's'}`, color: p.dark ? '#60A5FA' : '#2563EB' },
                  { label: `${stats.sections} section${stats.sections === 1 ? '' : 's'}`, color: p.dark ? '#A78BFA' : '#7C3AED' },
                  { label: `${stats.required} required`, color: p.dark ? '#F87171' : '#DC2626' },
                  { label: `${stats.totalRisks} risk${stats.totalRisks === 1 ? '' : 's'} defined`, color: p.dark ? '#FBBF24' : '#D97706' },
                ].map(s => (
                  <span key={s.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: s.color, background: `${s.color}${p.dark ? '22' : '12'}`, border: `1px solid ${s.color}30`, padding: '3px 10px', borderRadius: 99 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 99, background: s.color }} />
                    {s.label}
                  </span>
                ))}
              </div>
            </div>

            {list.length === 0 && (
              <div style={{ background: p.surface, border: `1px dashed ${p.lineStrong}`, borderRadius: 12, padding: '36px 24px', textAlign: 'center' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: p.accentSoft, color: p.accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  <Icon d={I.layers} size={20} />
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: p.ink, marginBottom: 6 }}>Add your first question</div>
                <div style={{ fontSize: 13, color: p.slate, maxWidth: 440, margin: '0 auto 20px', lineHeight: 1.55 }}>
                  Pick a question type to start. You can group questions into sections, make them required, show them conditionally, and attach a risk to any answer.
                </div>
                {canEdit && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, maxWidth: 580, margin: '0 auto' }}>
                    {TYPES.map(t => (
                      <button key={t.key} onClick={() => insertAt(0, blankItem(t.key, 'General'))} className="qb-hov"
                        style={{ textAlign: 'left', background: p.surface, border: `1px solid ${p.line}`, borderRadius: 10, padding: '14px 14px', cursor: 'pointer', fontFamily: sans }}>
                        <span style={{ color: typeColor(p, t.key), background: typeSoft(p, t.key), width: 34, height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}><Icon d={typeIcon(t.key)} size={17} /></span>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: p.ink }}>{t.label}</div>
                        <div style={{ fontSize: 12, color: p.slate, marginTop: 2 }}>{t.hint}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {runs.map(run => (
              <section key={run.start} style={{ marginBottom: 26 }}>
                <SectionHeader
                  p={p} name={run.section} count={run.items.length} canEdit={canEdit}
                  onRename={name => renameSection(run.start, run.items.length, name)}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {run.items.map(it => {
                    const n = numberOf.get(it.localKey)!;
                    const idx = n - 1;
                    const trig = triggerKeyOf(it.depends_on);
                    const trigItem = trig ? byQKey.get(trig) : undefined;
                    return (
                      <QuestionCard
                        key={it.localKey}
                        p={p} item={it} number={n} canEdit={canEdit}
                        selected={selectedKey === it.localKey}
                        autoFocus={focusKey === it.localKey}
                        issues={issuesByKey.get(it.localKey) || []}
                        risks={risksFor(it)}
                        responses={it.id ? inUse[it.id] || 0 : 0}
                        candidates={triggerCandidates(it)}
                        numberOf={numberOf}
                        triggerLabel={trigItem
                          ? (trigItem.options.some(o => isYesText(o.text))
                            ? `Shown when Q${numberOf.get(trigItem.localKey)} is Yes`
                            : `Shown once Q${numberOf.get(trigItem.localKey)} is answered`)
                          : null}
                        sectionNames={sectionNames}
                        isFirst={idx === 0} isLast={idx === list.length - 1}
                        dropLine={dropLine(it.localKey)} dragging={drag?.key === it.localKey}
                        cardRef={el => { if (el) cardRefs.current.set(it.localKey, el); else cardRefs.current.delete(it.localKey); }}
                        onSelect={() => select(it.localKey)}
                        onUpdate={patch => updateItem(it.localKey, patch)}
                        onRemove={() => removeItem(it.localKey)}
                        onDuplicate={() => duplicateItem(it.localKey)}
                        onMove={dir => moveItem(it.localKey, dir)}
                        onOpenRisk={option => it.id && setRiskModal({ itemId: it.id, option, questionText: it.question_text })}
                        dnd={{ start: dnd.start(it.localKey), over: dnd.over(it.localKey), drop: dnd.drop(it.localKey), end: dnd.end }}
                      />
                    );
                  })}
                </div>
                {canEdit && (
                  <AddQuestionBar p={p} onAdd={type => insertAt(run.start + run.items.length, blankItem(type, run.section))} />
                )}
              </section>
            ))}

            {canEdit && list.length > 0 && (
              <button onClick={() => insertAt(list.length, blankItem('radio', nextSectionName(sectionNames)))} className="qb-hov"
                style={{ width: '100%', padding: '14px', borderRadius: 10, border: `1px dashed ${p.lineStrong}`, background: 'transparent', color: p.slate, fontSize: 13, fontWeight: 600, fontFamily: sans, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Icon d={I.layers} size={14} /> Add a new section
              </button>
            )}
          </div>
        </main>

        {/* ── summary ── */}
        <aside className="qb-pane qb-summary" style={{ background: p.surface, borderLeft: `1px solid ${p.line}`, padding: '16px 18px 40px' }}>
          <SummaryPanel p={p} stats={stats} issues={issues} numberOf={numberOf} onJump={key => select(key, true)} />
        </aside>
      </div>

      {riskModal && (
        <RiskModal
          p={p}
          itemId={riskModal.itemId}
          option={riskModal.option}
          questionText={riskModal.questionText}
          existing={(risksByItem[riskModal.itemId] || []).find(r => r.option_text === riskModal.option) || null}
          onClose={() => setRiskModal(null)}
        />
      )}

      {toast && (
        <div key={toast.id} style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 1400, animation: 'qbToastIn .18s ease-out', background: isDark ? '#e5e7eb' : T.ink, color: isDark ? T.ink : '#fff', borderRadius: 10, padding: '10px 14px 10px 16px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 14, fontFamily: sans, boxShadow: '0 8px 24px rgba(0,0,0,.18)' }}>
          {toast.msg}
          {toast.undo && (
            <button onClick={() => { toast.undo!(); setToast(null); }} style={{ background: 'none', border: 'none', color: isDark ? '#2563EB' : '#93C5FD', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: sans, padding: 0 }}>Undo</button>
          )}
          <button onClick={() => setToast(null)} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: 'inherit', opacity: 0.6, cursor: 'pointer', padding: 0, display: 'inline-flex' }}><Icon d={I.close} size={13} /></button>
        </div>
      )}
    </div>
  );
}

const worstSeverity = (risks: Risk[]): Severity => {
  for (const s of SEVERITIES) if (risks.some(r => r.severity === s.key)) return s.key;
  return 'info';
};
const nextSectionName = (existing: string[]) => {
  let n = existing.length + 1;
  while (existing.includes(`Section ${n}`)) n++;
  return `Section ${n}`;
};

/* ─── shared bits ─────────────────────────────────────────────────────────────────────────── */

function AutoTextarea({ value, style, ...rest }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return <textarea ref={ref} rows={1} value={value} {...rest} style={{ ...style, resize: 'none', overflow: 'hidden' }} />;
}

function Switch({ p, checked, onChange, disabled, label }: { p: Pal; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{ width: 32, height: 18, borderRadius: 99, border: 'none', padding: 2, cursor: disabled ? 'default' : 'pointer', background: checked ? p.accent : p.lineStrong, opacity: disabled ? 0.5 : 1, transition: 'background .15s', flexShrink: 0, display: 'inline-flex' }}>
      <span style={{ width: 14, height: 14, borderRadius: 99, background: '#fff', transform: checked ? 'translateX(14px)' : 'translateX(0)', transition: 'transform .15s', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
    </button>
  );
}

function SectionHeader({ p, name, count, canEdit, onRename }: { p: Pal; name: string; count: number; canEdit: boolean; onRename: (n: string) => void }) {
  // Commits on blur/Enter, not per keystroke: typing a name that briefly matches a neighbouring
  // section would otherwise merge the two mid-edit.
  const [draft, setDraft] = useState(name);
  useEffect(() => setDraft(name), [name]);
  const commit = () => { const v = draft.trim(); if (v !== name) onRename(v); };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <span style={{ width: 28, height: 28, borderRadius: 8, background: p.surface, border: `1px solid ${p.line}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <img src="/logo-mark.png" alt="" draggable={false} style={{ width: 18, height: 18, objectFit: 'contain', userSelect: 'none', pointerEvents: 'none' }} />
      </span>
      <input value={draft} disabled={!canEdit} placeholder="Untitled section"
        onChange={e => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setDraft(name); (e.target as HTMLInputElement).blur(); } }}
        className="qb-ghost-input" title={canEdit ? 'Rename section' : undefined}
        style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: p.ink, fontFamily: sans, background: 'transparent', border: 'none', outline: 'none', borderRadius: 6, padding: '4px 6px' }} />
      <span style={{ fontSize: 12, color: p.muted, flexShrink: 0 }}>{count} question{count === 1 ? '' : 's'}</span>
    </div>
  );
}

function AddQuestionBar({ p, onAdd }: { p: Pal; onAdd: (t: QuestionType) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: p.muted, marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon d={I.plus} size={12} /> Add question</span>
      {TYPES.map(t => (
        <button key={t.key} onClick={() => onAdd(t.key)} title={t.hint} style={{ ...btn(p, 'secondary', 'sm'), color: typeColor(p, t.key), background: typeSoft(p, t.key), borderColor: `${typeColor(p, t.key)}35` }}>
          <Icon d={typeIcon(t.key)} size={12} /> {t.label}
        </button>
      ))}
    </div>
  );
}

/* ─── question card ───────────────────────────────────────────────────────────────────────── */

interface CardProps {
  p: Pal;
  item: Item;
  number: number;
  canEdit: boolean;
  selected: boolean;
  autoFocus: boolean;
  issues: Issue[];
  risks: Risk[];
  responses: number;
  candidates: Item[];
  numberOf: Map<string, number>;
  triggerLabel: string | null;
  sectionNames: string[];
  isFirst: boolean;
  isLast: boolean;
  dropLine: 'before' | 'after' | null;
  dragging: boolean;
  cardRef: (el: HTMLDivElement | null) => void;
  onSelect: () => void;
  onUpdate: (patch: Partial<Item>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMove: (dir: -1 | 1) => void;
  onOpenRisk: (option: string) => void;
  dnd: { start: (e: React.DragEvent) => void; over: (e: React.DragEvent) => void; drop: (e: React.DragEvent) => void; end: () => void };
}

function QuestionCard(props: CardProps) {
  const { p, item: it, number, canEdit, selected, issues, risks, dropLine, dragging } = props;
  const editing = selected && canEdit;
  const hasError = issues.some(i => i.level === 'error');
  const hasWarn = !hasError && issues.length > 0;
  const borderColor = selected ? p.accent : hasError ? `${p.red}70` : p.line;
  const riskFor = (o: Option) => risks.find(r => r.option_text === (o.orig ?? o.text));

  return (
    <div
      ref={props.cardRef}
      className={`qb-card${selected ? ' qb-sel' : ''}`}
      onClick={() => !selected && props.onSelect()}
      onDragOver={props.dnd.over} onDrop={props.dnd.drop}
      style={{
        position: 'relative', overflow: 'visible', background: p.surface, borderRadius: 12, border: `1px solid ${borderColor}`,
        boxShadow: selected ? `0 0 0 3px ${p.accentSoft}, 0 8px 22px rgba(37,99,235,.12)` : '0 1px 2px rgba(15,23,42,.04)',
        cursor: selected ? 'default' : 'pointer', opacity: dragging ? 0.4 : 1, transition: 'box-shadow .15s, border-color .15s',
        marginLeft: it.depends_on ? 22 : 0,
      }}>
      {dropLine && <div style={{ position: 'absolute', left: 0, right: 0, [dropLine === 'before' ? 'top' : 'bottom']: -7, height: 3, borderRadius: 3, background: p.accent }} />}

      <div style={{ display: 'flex', gap: 10, padding: editing ? '16px 18px 14px 10px' : '14px 18px 14px 10px' }}>
        {/* grip + number */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 30, flexShrink: 0 }}>
          {canEdit ? (
            <span className="qb-grip" draggable onDragStart={props.dnd.start} onDragEnd={props.dnd.end} title="Drag to reorder"
              style={{ cursor: 'grab', color: p.muted, display: 'inline-flex', padding: 2 }}>
              <Icon d={I.grip} size={16} sw={3} />
            </span>
          ) : <span style={{ height: 20 }} />}
          <span style={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums', padding: '2px 6px', borderRadius: 6, color: selected ? '#fff' : typeColor(p, it.question_type), background: selected ? p.accent : typeSoft(p, it.question_type) }}>Q{number}</span>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <CardEditor {...props} riskFor={riskFor} />
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 600, color: it.question_text.trim() ? p.ink : p.muted, fontStyle: it.question_text.trim() ? 'normal' : 'italic', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {it.question_text.trim() || 'Untitled question'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
                <Chip p={p} icon={typeIcon(it.question_type)} color={typeColor(p, it.question_type)}>{typeLabel(it.question_type)}</Chip>
                {it.required && <Chip p={p} tone="red">Required</Chip>}
                {props.responses > 0 && <Chip p={p}>Answered on {props.responses} questionnaire{props.responses === 1 ? '' : 's'}</Chip>}
                {props.triggerLabel && <Chip p={p} icon={I.logic} tone="accent">{props.triggerLabel}</Chip>}
                {hasError && <Chip p={p} icon={I.warn} tone="red">{issues.find(i => i.level === 'error')!.message}</Chip>}
                {hasWarn && <Chip p={p} icon={I.warn} tone="amber">{issues[0].message}</Chip>}
              </div>
              {it.question_type !== 'text' && it.options.some(o => o.text.trim()) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {it.options.filter(o => o.text.trim()).map(o => {
                    const r = riskFor(o);
                    return (
                      <span key={o.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 500, color: p.ink, padding: '4px 10px', borderRadius: 99, border: `1px solid ${r ? severityMeta(r.severity).color + '66' : typeColor(p, it.question_type) + '30'}`, background: r ? severityMeta(r.severity).color + '12' : typeSoft(p, it.question_type) }}>
                        <span style={{ width: 10, height: 10, borderRadius: it.question_type === 'radio' ? 99 : 3, border: `1.5px solid ${typeColor(p, it.question_type)}`, flexShrink: 0 }} />
                        {o.text}
                        {r && <span title={`${severityMeta(r.severity).label} risk: ${r.finding}`} style={{ color: severityMeta(r.severity).color, display: 'inline-flex' }}><Icon d={I.shield} size={11} /></span>}
                      </span>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ p, icon, tone, color, children }: { p: Pal; icon?: string; tone?: 'accent' | 'red' | 'amber'; color?: string; children: React.ReactNode }) {
  if (color) tone = 'accent';
  const c = color ? { fg: color, bg: `${color}${p.dark ? '26' : '14'}` } : tone === 'accent' ? { fg: p.accent, bg: p.accentSoft } : tone === 'red' ? { fg: p.red, bg: p.redSoft } : tone === 'amber' ? { fg: p.amber, bg: p.amberSoft } : { fg: p.slate, bg: p.surface2 };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, color: c.fg, background: c.bg, padding: '3px 8px', borderRadius: 6, border: tone ? 'none' : `1px solid ${p.line}` }}>
      {icon && <Icon d={icon} size={11} />}
      {children}
    </span>
  );
}

function CardEditor(props: CardProps & { riskFor: (o: Option) => Risk | undefined }) {
  const { p, item: it, risks, candidates, numberOf, issues, riskFor, onUpdate } = props;
  const optionRefs = useRef(new Map<string, HTMLInputElement>());
  const [focusOption, setFocusOption] = useState<string | null>(null);
  useEffect(() => {
    if (!focusOption) return;
    optionRefs.current.get(focusOption)?.focus();
    setFocusOption(null);
  }, [focusOption, it.options]);

  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '8px 11px', fontSize: 13.5, fontFamily: sans, color: p.ink,
    background: p.surface, border: `1px solid ${p.line}`, borderRadius: 8, outline: 'none',
  };

  const setOptions = (options: Option[]) => onUpdate({ options });
  const changeType = (t: QuestionType) => {
    if (t === it.question_type) return;
    if (t === 'text' && risks.length && !window.confirm(`This question has ${risks.length} risk${risks.length === 1 ? '' : 's'} attached to its options. Switching to a text answer removes them when you save. Continue?`)) return;
    onUpdate({ question_type: t, options: t === 'text' ? [] : (it.options.length ? it.options : newOptions(['Yes', 'No'])) });
  };
  const addOptionAfter = (idx: number) => {
    const o: Option = { key: uid(), text: '', orig: null };
    const next = [...it.options];
    next.splice(idx + 1, 0, o);
    setOptions(next);
    setFocusOption(o.key);
  };
  const removeOption = (idx: number, focusPrev = false) => {
    const o = it.options[idx];
    const r = riskFor(o);
    if (r && !window.confirm(`"${o.orig ?? o.text}" has a ${severityMeta(r.severity).label.toLowerCase()} risk attached. Removing the option deletes the risk when you save. Continue?`)) return;
    const next = it.options.filter((_, i) => i !== idx);
    setOptions(next);
    if (focusPrev && next[idx - 1]) setFocusOption(next[idx - 1].key);
  };
  const applyPreset = (texts: string[]) => {
    const existing = new Map(it.options.map(o => [o.text.trim().toLowerCase(), o]));
    setOptions(texts.map(t => existing.get(t.toLowerCase()) || { key: uid(), text: t, orig: null }));
  };

  const logicOn = !!it.depends_on;
  const trigKey = triggerKeyOf(it.depends_on);
  const nearestCandidate = () => {
    const myN = numberOf.get(it.localKey) || 0;
    const before = candidates.filter(c => (numberOf.get(c.localKey) || 0) < myN);
    return (before[before.length - 1] || candidates[0]) as Item | undefined;
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      {/* type + required */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div role="radiogroup" aria-label="Question type" style={{ display: 'inline-flex', background: p.surface2, border: `1px solid ${p.line}`, borderRadius: 8, padding: 2 }}>
          {TYPES.map(t => {
            const on = it.question_type === t.key;
            return (
              <button key={t.key} role="radio" aria-checked={on} onClick={() => changeType(t.key)} title={t.hint}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: sans, fontSize: 12, fontWeight: 600, background: on ? typeColor(p, t.key) : 'transparent', color: on ? '#fff' : typeColor(p, t.key), boxShadow: on ? `0 2px 6px ${typeColor(p, t.key)}40` : 'none' }}>
                <Icon d={typeIcon(t.key)} size={12} /> {t.label}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1 }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: p.ink, fontWeight: 600, cursor: 'pointer' }}>
          Required
          <Switch p={p} checked={it.required} onChange={v => onUpdate({ required: v })} label="Required" />
        </label>
      </div>

      {/* question text */}
      <AutoTextarea
        autoFocus={props.autoFocus}
        value={it.question_text}
        onChange={e => onUpdate({ question_text: e.target.value })}
        placeholder="Type your question…"
        className="qb-input"
        style={{ ...input, fontSize: 15, fontWeight: 600, lineHeight: 1.5, padding: '10px 12px', minHeight: 46 }}
      />

      {/* answers */}
      <div style={{ marginTop: 14 }}>
        <div style={{ ...eyebrow(p), marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          {it.question_type === 'text' ? 'Answer field' : 'Answer options'}
          {it.question_type !== 'text' && <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: p.muted }}>· attach a risk to any option the vendor shouldn't pick</span>}
        </div>

        {it.question_type === 'text' ? (
          <div style={{ border: `1px dashed ${p.lineStrong}`, borderRadius: 8, padding: '12px 12px', fontSize: 13, color: p.muted, background: p.surface2 }}>
            The vendor will type a free-form answer here.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {it.options.map((o, oi) => {
              const r = riskFor(o);
              const renamedUnsaved = !!o.orig && o.text.trim() !== o.orig;
              const riskDisabledReason = !it.id ? 'Save the questionnaire to attach risks to this question'
                : !o.orig ? 'Save the questionnaire to attach a risk to this new option'
                : renamedUnsaved ? 'Save to finish renaming this option before editing its risk'
                : null;
              return (
                <div key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: it.question_type === 'radio' ? 99 : 4, border: `1.5px solid ${p.lineStrong}`, flexShrink: 0 }} />
                  <input
                    ref={el => { if (el) optionRefs.current.set(o.key, el); else optionRefs.current.delete(o.key); }}
                    value={o.text}
                    onChange={e => setOptions(it.options.map((x, i) => (i === oi ? { ...x, text: e.target.value } : x)))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); addOptionAfter(oi); }
                      if (e.key === 'Backspace' && !o.text && it.options.length > 1) { e.preventDefault(); removeOption(oi, true); }
                    }}
                    placeholder={`Option ${oi + 1}`}
                    className="qb-input"
                    style={{ ...input, flex: 1 }}
                  />
                  {r ? (
                    <button onClick={() => !riskDisabledReason && props.onOpenRisk(o.orig as string)} disabled={!!riskDisabledReason} title={riskDisabledReason || `Edit risk: ${r.finding}`}
                      style={{ ...btn(p, 'secondary', 'sm'), minWidth: 104, justifyContent: 'flex-start', color: severityMeta(r.severity).color, borderColor: severityMeta(r.severity).color + '55', background: severityMeta(r.severity).color + '12', opacity: riskDisabledReason ? 0.6 : 1 }}>
                      <Icon d={I.shield} size={12} /> {severityMeta(r.severity).label} risk
                    </button>
                  ) : (
                    <button onClick={() => !riskDisabledReason && o.text.trim() && props.onOpenRisk(o.text.trim())} disabled={!!riskDisabledReason || !o.text.trim()} title={riskDisabledReason || 'Attach a risk to this answer'}
                      className="qb-hov" style={{ ...btn(p, 'ghost', 'sm'), minWidth: 104, justifyContent: 'flex-start', opacity: riskDisabledReason || !o.text.trim() ? 0.45 : 1, cursor: riskDisabledReason ? 'not-allowed' : 'pointer' }}>
                      <Icon d={I.shield} size={12} /> Add risk
                    </button>
                  )}
                  <button onClick={() => removeOption(oi)} disabled={it.options.length <= 1} title="Remove option" className="qb-hov" style={{ ...iconBtn(p), opacity: it.options.length <= 1 ? 0.3 : 1 }}>
                    <Icon d={I.close} size={13} />
                  </button>
                </div>
              );
            })}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2, paddingLeft: 22 }}>
              <button onClick={() => addOptionAfter(it.options.length - 1)} style={{ ...btn(p, 'ghost', 'sm'), color: p.accent, paddingLeft: 0 }}>
                <Icon d={I.plus} size={12} /> Add option
              </button>
              <span style={{ fontSize: 11.5, color: p.muted }}>Quick fill:</span>
              {[['Yes', 'No'], ['Yes', 'No', 'N/A'], ['Yes', 'Partially', 'No']].map(set => (
                <button key={set.join('/')} onClick={() => applyPreset(set)} className="qb-hov" style={{ ...btn(p, 'secondary', 'sm'), fontSize: 11.5, padding: '3px 8px', color: p.slate }}>
                  {set.join(' / ')}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* logic */}
      <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: p.surface2, border: `1px solid ${p.line}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: logicOn ? p.accent : p.muted, display: 'inline-flex' }}><Icon d={I.logic} size={14} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: p.ink }}>Conditional logic</div>
            <div style={{ fontSize: 12, color: p.muted }}>
              {candidates.length || logicOn ? 'Only ask this question when an earlier answer is "Yes"' : 'Needs a saved single- or multi-select question with a "Yes" option to act as the trigger'}
            </div>
          </div>
          <Switch p={p} checked={logicOn} disabled={!logicOn && candidates.length === 0} label="Conditional logic"
            onChange={v => {
              if (!v) { onUpdate({ depends_on: null, visibility: 'visible' }); return; }
              const c = nearestCandidate();
              if (c?.questionKey) onUpdate({ depends_on: c.questionKey, visibility: 'hidden' });
            }} />
        </div>
        {logicOn && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10, paddingLeft: 24, fontSize: 12.5, color: p.slate }}>
            Show when
            <select value={trigKey || ''} onChange={e => onUpdate({ depends_on: e.target.value || null, visibility: e.target.value ? 'hidden' : 'visible' })}
              className="qb-input" style={{ ...input, width: 'auto', maxWidth: 360, padding: '6px 10px', fontSize: 12.5 }}>
              {trigKey && !candidates.some(c => c.questionKey === trigKey) && <option value={trigKey}>(question unavailable)</option>}
              {candidates.map(c => (
                <option key={c.localKey} value={c.questionKey}>Q{numberOf.get(c.localKey)} · {(c.question_text || 'Untitled').slice(0, 48)}</option>
              ))}
            </select>
            is answered <strong style={{ color: p.ink }}>Yes</strong>
          </div>
        )}
      </div>

      {issues.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {issues.map((i, k) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: i.level === 'error' ? p.red : p.amber }}>
              <Icon d={I.warn} size={12} /> {i.message}
            </div>
          ))}
        </div>
      )}

      {/* footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${p.line}`, flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: p.slate, marginRight: 'auto' }}>
          Section
          <input value={it.section} list="qb-sections" onChange={e => onUpdate({ section: e.target.value })} placeholder="Untitled section"
            className="qb-input" style={{ ...input, width: 180, padding: '5px 9px', fontSize: 12.5 }} />
          <datalist id="qb-sections">{props.sectionNames.map(s => <option key={s} value={s} />)}</datalist>
        </label>
        <button onClick={() => props.onMove(-1)} disabled={props.isFirst} title="Move up" className="qb-hov" style={{ ...iconBtn(p), opacity: props.isFirst ? 0.3 : 1 }}><Icon d={I.up} /></button>
        <button onClick={() => props.onMove(1)} disabled={props.isLast} title="Move down" className="qb-hov" style={{ ...iconBtn(p), opacity: props.isLast ? 0.3 : 1 }}><Icon d={I.down} /></button>
        <button onClick={props.onDuplicate} title="Duplicate" className="qb-hov" style={iconBtn(p)}><Icon d={I.copy} /></button>
        <span style={{ width: 1, height: 18, background: p.line, margin: '0 4px' }} />
        <button onClick={props.onRemove}
          title={props.responses ? `Can't delete: vendors have responded to this question on ${props.responses} questionnaire${props.responses === 1 ? '' : 's'}` : 'Delete question'}
          className="qb-hov" style={{ ...iconBtn(p, p.red), opacity: props.responses ? 0.35 : 1, cursor: props.responses ? 'not-allowed' : 'pointer' }}><Icon d={I.trash} /></button>
      </div>
    </div>
  );
}

/* ─── summary panel ───────────────────────────────────────────────────────────────────────── */

function SummaryPanel({ p, stats, issues, numberOf, onJump }: {
  p: Pal;
  stats: { total: number; required: number; conditional: number; sections: number; byType: Record<QuestionType, number>; totalRisks: number; bySeverity: Record<Severity, number>; choice: number; withRisks: number };
  issues: Issue[];
  numberOf: Map<string, number>;
  onJump: (key: string) => void;
}) {
  const errors = issues.filter(i => i.level === 'error');
  const warnings = issues.filter(i => i.level === 'warning');
  const block: React.CSSProperties = { paddingBottom: 18, marginBottom: 18, borderBottom: `1px solid ${p.line}` };
  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0' }}>
      <span style={{ fontSize: 12.5, color: p.slate }}>{label}</span>
      <span style={{ fontSize: 13.5, fontWeight: 700, color: p.ink, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );

  return (
    <div>
      <div style={block}>
        <div style={{ ...eyebrow(p), marginBottom: 10 }}>Readiness</div>
        {stats.total === 0 ? (
          <div style={{ fontSize: 12.5, color: p.muted }}>Add questions to get started.</div>
        ) : issues.length === 0 ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 9, background: p.greenSoft, color: p.green }}>
            <Icon d={I.ok} size={16} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Ready to send</div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>Every question is complete.</div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1, padding: '8px 10px', borderRadius: 8, background: errors.length ? p.redSoft : p.surface2, color: errors.length ? p.red : p.muted }}>
                <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>{errors.length}</div>
                <div style={{ fontSize: 11.5 }}>to fix before saving</div>
              </div>
              <div style={{ flex: 1, padding: '8px 10px', borderRadius: 8, background: warnings.length ? p.amberSoft : p.surface2, color: warnings.length ? p.amber : p.muted }}>
                <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>{warnings.length}</div>
                <div style={{ fontSize: 11.5 }}>warning{warnings.length === 1 ? '' : 's'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[...errors, ...warnings].slice(0, 8).map((i, k) => (
                <button key={k} onClick={() => onJump(i.key)} className="qb-hov"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left', background: 'transparent', border: 'none', borderRadius: 6, padding: '6px 6px', cursor: 'pointer', fontFamily: sans }}>
                  <span style={{ color: i.level === 'error' ? p.red : p.amber, display: 'inline-flex', marginTop: 1 }}><Icon d={I.warn} size={12} /></span>
                  <span style={{ fontSize: 12, color: p.slate, lineHeight: 1.45 }}><strong style={{ color: p.ink }}>Q{numberOf.get(i.key)}</strong> · {i.message}</span>
                </button>
              ))}
              {issues.length > 8 && <div style={{ fontSize: 11.5, color: p.muted, padding: '4px 6px' }}>+{issues.length - 8} more</div>}
            </div>
          </>
        )}
      </div>

      <div style={block}>
        <div style={{ ...eyebrow(p), marginBottom: 8 }}>Questions</div>
        {row('Total', stats.total)}
        {row('Required', stats.required)}
        {row('Conditional', stats.conditional)}
        {row('Sections', stats.sections)}
        {stats.total > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
            {TYPES.map(t => {
              const v = stats.byType[t.key];
              return (
                <div key={t.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: p.slate, marginBottom: 3 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ color: typeColor(p, t.key), display: 'inline-flex' }}><Icon d={typeIcon(t.key)} size={11} /></span> {t.label}</span>
                    <span style={{ fontWeight: 600, color: p.ink }}>{v}</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 99, background: typeSoft(p, t.key), overflow: 'hidden' }}>
                    <div style={{ width: `${(v / stats.total) * 100}%`, height: '100%', background: typeColor(p, t.key), borderRadius: 99 }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ ...eyebrow(p), marginBottom: 8 }}>Risks</div>
        {row('Total risks', stats.totalRisks)}
        {row('Choice questions with a risk', `${stats.withRisks} / ${stats.choice}`)}
        <div style={{ display: 'flex', height: 8, borderRadius: 99, overflow: 'hidden', background: p.surface2, margin: '10px 0 10px' }}>
          {stats.totalRisks > 0 && SEVERITIES.map(s => stats.bySeverity[s.key] > 0 && (
            <div key={s.key} title={`${s.label}: ${stats.bySeverity[s.key]}`} style={{ width: `${(stats.bySeverity[s.key] / stats.totalRisks) * 100}%`, background: s.color }} />
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {SEVERITIES.map(s => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: s.color, flexShrink: 0 }} />
              <span style={{ color: p.slate, flex: 1 }}>{s.label}</span>
              <span style={{ color: stats.bySeverity[s.key] ? p.ink : p.muted, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{stats.bySeverity[s.key]}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: p.muted, lineHeight: 1.6 }}>
        Risks are recorded against the vendor's answers for review. They aren't factored into any score.
      </div>
      <div style={{ fontSize: 11.5, color: p.muted, lineHeight: 1.6, marginTop: 10 }}>
        Tip: press <kbd style={{ fontFamily: 'inherit', fontSize: 11, padding: '1px 5px', border: `1px solid ${p.line}`, borderRadius: 4, background: p.surface2 }}>Ctrl</kbd> + <kbd style={{ fontFamily: 'inherit', fontSize: 11, padding: '1px 5px', border: `1px solid ${p.line}`, borderRadius: 4, background: p.surface2 }}>S</kbd> to save. Press Enter in an option to add the next one.
      </div>
    </div>
  );
}

/* ─── risk dialog ─────────────────────────────────────────────────────────────────────────── */

function RiskModal({ p, itemId, option, questionText, existing, onClose }: {
  p: Pal; itemId: string; option: string; questionText: string; existing: Risk | null; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [finding, setFinding] = useState(existing?.finding || '');
  const [riskStatement, setRiskStatement] = useState(existing?.risk_statement || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [severity, setSeverity] = useState<Severity>(existing?.severity || 'medium');
  const [compensating, setCompensating] = useState(existing?.request_compensating_control || false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['questionnaire-library', 'risks'] });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = { option_text: option, finding: finding.trim(), risk_statement: riskStatement.trim(), description: description.trim(), severity, request_compensating_control: compensating };
      return existing
        ? (await api.patch(`/v1/questionnaire-library/risks/${existing.id}`, body)).data
        : (await api.post(`/v1/questionnaire-library/items/${itemId}/risks`, body)).data;
    },
    onSuccess: () => { invalidate(); onClose(); },
    onError: err => setError(apiError(err, 'Could not save this risk. Try again.')),
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/v1/questionnaire-library/risks/${existing!.id}`),
    onSuccess: () => { invalidate(); onClose(); },
    onError: err => setError(apiError(err, 'Could not delete this risk. Try again.')),
  });

  const busy = saveMutation.isPending || deleteMutation.isPending;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const sev = severityMeta(severity);
  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '9px 11px', fontSize: 13.5, fontFamily: sans, color: p.ink,
    background: p.surface, border: `1px solid ${p.line}`, borderRadius: 8, outline: 'none',
  };
  const field = (label: string, hint: string, el: React.ReactNode) => (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: p.ink }}>{label} <span style={{ color: p.red }}>*</span></div>
      <div style={{ fontSize: 12, color: p.muted, margin: '2px 0 6px' }}>{hint}</div>
      {el}
    </div>
  );

  const save = () => {
    if (!finding.trim() || !riskStatement.trim() || !description.trim()) { setError('Finding, risk and description are all required.'); return; }
    setError('');
    saveMutation.mutate();
  };

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 20 }}>
      <div role="dialog" aria-modal="true" aria-label={existing ? 'Edit risk' : 'Add risk'}
        style={{ width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: p.surface, border: `1px solid ${p.line}`, borderRadius: 14, fontFamily: sans, boxShadow: '0 20px 50px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '18px 22px 16px', borderBottom: `1px solid ${p.line}`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ width: 36, height: 36, borderRadius: 10, background: sev.color + '1A', color: sev.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon d={I.shield} size={18} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: p.ink }}>{existing ? 'Edit risk' : 'Add risk'}</div>
            <div style={{ fontSize: 12.5, color: p.slate, marginTop: 3, lineHeight: 1.5 }}>
              Raised when the vendor answers <span style={{ fontWeight: 700, color: p.ink, background: p.surface2, border: `1px solid ${p.line}`, borderRadius: 5, padding: '0 6px' }}>{option}</span>
            </div>
            {questionText.trim() && <div style={{ fontSize: 12, color: p.muted, marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>to “{questionText.trim()}”</div>}
          </div>
          <button onClick={() => !busy && onClose()} aria-label="Close" className="qb-hov" style={iconBtn(p)}><Icon d={I.close} /></button>
        </div>

        <div style={{ padding: '18px 22px', display: 'grid', gap: 16, overflowY: 'auto' }}>
          {field('Finding', 'What did you observe that could lead to a risk?',
            <input autoFocus value={finding} onChange={e => setFinding(e.target.value)} placeholder="E.g. No documented incident response procedure" className="qb-input" style={input} />)}
          {field('Risk', 'What is the impact if this finding is present?',
            <input value={riskStatement} onChange={e => setRiskStatement(e.target.value)} placeholder="E.g. Security incidents may not be contained in time" className="qb-input" style={input} />)}
          {field('Description', 'Why does this create a risk, and how can the vendor remediate it?',
            <AutoTextarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Explain the risk and the expected remediation…" className="qb-input" style={{ ...input, minHeight: 88, lineHeight: 1.5 }} />)}

          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: p.ink }}>Severity</div>
            <div style={{ fontSize: 12, color: p.muted, margin: '2px 0 8px' }}>How serious is this gap?</div>
            <div role="radiogroup" aria-label="Severity" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
              {SEVERITIES.map(s => {
                const on = severity === s.key;
                return (
                  <button key={s.key} role="radio" aria-checked={on} onClick={() => setSeverity(s.key)}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 4px', borderRadius: 8, cursor: 'pointer', fontFamily: sans, fontSize: 12.5, fontWeight: 600, border: `1px solid ${on ? s.color : p.line}`, background: on ? s.color + '16' : p.surface, color: on ? s.color : p.slate }}>
                    <span style={{ width: 8, height: 8, borderRadius: 99, background: s.color }} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', borderRadius: 10, background: p.surface2, border: `1px solid ${p.line}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: p.ink }}>Request compensating controls</div>
              <div style={{ fontSize: 12, color: p.muted, marginTop: 2, lineHeight: 1.5 }}>Ask the vendor to describe any controls that offset this risk when they give this answer.</div>
            </div>
            <Switch p={p} checked={compensating} onChange={setCompensating} label="Request compensating controls" />
          </div>

          {error && <div style={{ fontSize: 12.5, color: p.red, display: 'flex', alignItems: 'center', gap: 6 }}><Icon d={I.warn} size={13} /> {error}</div>}
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${p.line}`, background: p.surface2, borderRadius: '0 0 14px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          {existing && (confirmDelete ? (
            <>
              <span style={{ fontSize: 12.5, color: p.slate }}>Delete this risk?</span>
              <button onClick={() => deleteMutation.mutate()} disabled={busy} style={btn(p, 'danger', 'sm')}>{deleteMutation.isPending ? 'Deleting…' : 'Delete'}</button>
              <button onClick={() => setConfirmDelete(false)} disabled={busy} style={btn(p, 'ghost', 'sm')}>Keep</button>
            </>
          ) : (
            <button onClick={() => setConfirmDelete(true)} disabled={busy} style={{ ...btn(p, 'ghost', 'sm'), color: p.red, paddingLeft: 0 }}><Icon d={I.trash} size={13} /> Delete risk</button>
          ))}
          <div style={{ flex: 1 }} />
          <button onClick={() => !busy && onClose()} disabled={busy} style={btn(p)}>Cancel</button>
          <button onClick={save} disabled={busy} style={{ ...btn(p, 'primary'), opacity: busy ? 0.6 : 1 }}>
            {saveMutation.isPending ? 'Saving…' : existing ? 'Save risk' : 'Add risk'}
          </button>
        </div>
      </div>
    </div>
  );
}
